/**
 * Un WebSocket que sobrevive a la vida real.
 *
 * `new WebSocket(url)` son cuatro líneas y funciona en la máquina de un
 * desarrollador el tiempo de una demo. Lo que no sobrevive es la vida ordinaria
 * de una pestaña: cerrar la laptop, un cambio de wifi de camino a una reunión,
 * un deploy de la API, un balanceador recortando una conexión ociosa. Cada uno
 * de esos cierra el socket, y un cliente plano nunca vuelve. No se le dice al
 * usuario. La campana simplemente deja de contar, y el reporte que te llega es
 * «las notificaciones a veces no llegan», que es irreproducible por
 * construcción.
 *
 * Así que el código interesante aquí no es la conexión. Es:
 *
 * - **Reconexión con backoff exponencial y jitter.** Backoff porque un backend
 *   que acaba de caerse no debe ser golpeado por todas las pestañas abiertas a
 *   la vez; jitter porque sin él todas las pestañas de la empresa reintentan en
 *   el mismo segundo, que es precisamente la manada que el backoff quería
 *   evitar — un deploy que reinicia la API sería seguido por una tormenta de
 *   reconexiones sincronizadas cada dos, cuatro y luego ocho segundos.
 * - **Heartbeats.** Una conexión TCP puede estar muerta durante minutos con
 *   `readyState` aún reportando OPEN — una conexión semiabierta no produce
 *   ningún `onclose`. El único detector confiable es un ping sin respuesta.
 * - **Una señal de re-sync en cada reconexión.** Las tramas enviadas mientras
 *   estaba desconectado se fueron; el channel layer no tiene replay y no debe
 *   tenerlo. Así que al reconectar el cliente vuelve a pedir por HTTP en vez de
 *   asumir continuidad. Esta es la línea más importante del archivo: es lo que
 *   vuelve inofensiva una trama perdida.
 * - **Manejo de códigos de cierre.** 4001 significa «tu ticket fue rechazado»
 *   y merece un reintento inmediato con un ticket nuevo; 1006 significa «se fue
 *   la red» y merece un backoff. Tratarlos igual hace que un ticket expirado
 *   parezca una caída por la duración del backoff.
 *
 * Deliberadamente sin framework: sin import de React, un solo `WebSocket`, y
 * una lista plana de suscriptores. Eso lo hace testeable con un socket falso
 * (ver `tests/notificationSocket.test.ts`) y mantiene la lógica de reconexión
 * fuera de un `useEffect`, donde el doble-invoke de React 18 en desarrollo
 * abriría dos conexiones y donde cada cambio de dependencia es una reconexión
 * silenciosa.
 */
import type { ServerFrame, SocketStatus } from './types';
import { CLOSE_SCOPE_REVOKED, CLOSE_UNAUTHENTICATED, WIRE_VERSION } from './types';

export interface SocketOptions {
  /** Acuña un ticket de un solo uso. Inyectado para que los tests no necesiten HTTP. */
  getTicket: () => Promise<string>;
  /** Endpoint ws:// o wss:// absoluto. */
  url: string;
  onFrame: (frame: ServerFrame) => void;
  /**
   * Llamado tras cada conexión exitosa (re)conexión, incluida la primera.
   *
   * El handler vuelve a pedir el buzón. Dispara en la primera conexión también,
   * no solo en las reconexiones, porque la alternativa es un caso especial cuyo
   * único efecto es que el camino que nadie prueba se comporta distinto.
   */
  onResync: () => void;
  onStatus?: (status: SocketStatus) => void;
}

const HEARTBEAT_INTERVAL_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;
const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * Fallos de auth consecutivos antes de rendirse. Un reintento cubre el caso
 * ordinario: un ticket que expiró entre acuñarlo y conectar. Rechazos repetidos
 * significan que la sesión realmente se fue, y reintentar para siempre
 * golpearía el endpoint de tickets con una credencial que nunca va a servir.
 */
const MAX_AUTH_RETRIES = 2;

export class NotificationSocket {
  private socket: WebSocket | null = null;
  private options: SocketOptions;
  private attempt = 0;
  private authFailures = 0;
  private stopped = true;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: SocketOptions) {
    this.options = options;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    window.addEventListener('online', this.handleOnline);
    document.addEventListener('visibilitychange', this.handleVisibility);
    void this.open();
  }

  /** Idempotente, y debe serlo: el strict mode de React llama el cleanup dos veces. */
  stop(): void {
    this.stopped = true;
    window.removeEventListener('online', this.handleOnline);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.clearTimers();
    // 1000 = cierre normal. Cualquier otra cosa y algunos proxies lo loguean
    // como error.
    this.socket?.close(1000, 'client stop');
    this.socket = null;
    this.setStatus('closed');
  }

  private setStatus(status: SocketStatus): void {
    this.options.onStatus?.(status);
  }

  private async open(): Promise<void> {
    if (this.stopped) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let ticket: string;
    try {
      ticket = await this.options.getTicket();
    } catch {
      // El endpoint de tickets falló — normalmente porque la sesión se fue y
      // `apiClient` está a mitad de un refresh. Backoff antes que girar: la capa
      // de auth redirige al login por su cuenta si debe.
      this.scheduleReconnect();
      return;
    }
    if (this.stopped) return;

    const socket = new WebSocket(`${this.options.url}?ticket=${encodeURIComponent(ticket)}`);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.authFailures = 0;
      this.setStatus('open');
      this.startHeartbeat();
      // La verdad por HTTP. Lo que pasó mientras estábamos fuera no está en el
      // futuro de este socket.
      this.options.onResync();
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data) as ServerFrame;
      } catch {
        // Una trama que no podemos parsear no vale un throw: una excepción en
        // `onmessage` es tragada por el navegador y deja el socket abierto pero
        // la app sutilmente rota.
        return;
      }

      if (frame.v !== WIRE_VERSION) {
        // Una pestaña vieja contra una API nueva. Ignora la trama, pero
        // mantiene la conexión: `onResync` por HTTP sigue funcionando, así que
        // la pestaña degrada a «se actualiza cuando vuelve a pedir» en vez de
        // quedar a oscuras.
        return;
      }

      if (frame.type === 'pong') {
        this.clearPongTimer();
        return;
      }

      this.options.onFrame(frame);
    };

    socket.onclose = (event: CloseEvent) => {
      this.clearTimers();
      this.socket = null;
      if (this.stopped) return;

      if (event.code === CLOSE_SCOPE_REVOKED) {
        // El servidor colgó a propósito y un reintento sería rechazado igual.
        // Quien sea dueño de la sesión decide qué sigue.
        this.stop();
        return;
      }

      if (event.code === CLOSE_UNAUTHENTICATED) {
        this.authFailures += 1;
        if (this.authFailures > MAX_AUTH_RETRIES) {
          this.stop();
          return;
        }
        // Un ticket rechazado no es un problema de red: reintenta ahora con uno
        // nuevo en vez de servirle al usuario un backoff silencioso.
        this.setStatus('reconnecting');
        void this.open();
        return;
      }

      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.setStatus('reconnecting');

    // Jitter completo: un punto aleatorio en [0, exponencial con tope]. Elegido
    // sobre «retraso ± 20%» porque es la variante que de verdad descorrelaciona
    // clientes — con una banda estrecha, 300 pestañas siguen reintentando dentro
    // del mismo medio segundo.
    const ceiling = Math.min(BASE_BACKOFF_MS * 2 ** this.attempt, MAX_BACKOFF_MS);
    const delay = Math.random() * ceiling;
    this.attempt += 1;

    this.reconnectTimer = setTimeout(() => void this.open(), delay);
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) return;
      this.socket.send(JSON.stringify({ type: 'ping' }));

      // Si el pong no vuelve, la conexión está muerta diga lo que diga
      // `readyState`. Cerrarla nosotros es lo que convierte un socket
      // semiabierto en una reconexión.
      this.pongTimer = setTimeout(() => {
        this.socket?.close(4000, 'heartbeat timeout');
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private clearPongTimer(): void {
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pongTimer = null;
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.clearPongTimer();
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
  }

  /** La red volvió. No esperes un backoff de 30 segundos. */
  private handleOnline = (): void => {
    if (this.stopped || this.socket) return;
    this.clearTimers();
    this.attempt = 0;
    void this.open();
  };

  /**
   * Una pestaña que vuelve al primer plano es el otro caso que vale
   * cortocircuitar.
   *
   * Las pestañas en segundo plano tienen sus temporizadores limitados a
   * aproximadamente una vez por minuto, así que una pestaña restaurada después
   * de una hora dormida puede estar sentada en un reintento pendiente que no
   * disparará por otros 50 segundos — y sus datos llevan una hora de viejos.
   * El mismo throttling es por qué `onResync` importa más que el socket: una
   * pestaña en segundo plano pierde tramas tenga o no sobrevivido su conexión.
   */
  private handleVisibility = (): void => {
    if (this.stopped || document.visibilityState !== 'visible') return;
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.options.onResync();
      return;
    }
    this.handleOnline();
  };
}
